<template>
  <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
    <div class="bg-white border border-gray-300 rounded-full">
      <button
        @click="isFilterModalOpen = true"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-500 hover:bg-gray-50 transition-colors rounded-full"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M9.99993 13.2533C10.0266 13.4533 9.95993 13.6667 9.80659 13.8067C9.54659 14.0667 9.12659 14.0667 8.86659 13.8067L6.19326 11.1333C6.03993 10.98 5.97326 10.7733 5.99993 10.58V7.16667L2.80659 3.08C2.57993 2.79333 2.63326 2.37333 2.91993 2.14667C3.04659 2.05333 3.18659 2 3.33326 2H12.6666C12.8133 2 12.9533 2.05333 13.0799 2.14667C13.3666 2.37333 13.4199 2.79333 13.1933 3.08L9.99993 7.16667V13.2533ZM4.69326 3.33333L7.33326 6.70667V10.3867L8.66659 11.72V6.7L11.3066 3.33333H4.69326Z"
            fill="#808080"
          />
        </svg>
        Filtros
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div class="relative">
        <div class="flex items-center gap-2">
          <span class="text-xs sm:text-sm font-bold text-primary30 whitespace-nowrap">
            Ordenar por:
          </span>
          <OutlinedButton
            :text="currentSortLabel"
            height="32px"
            @click.stop="toggleSortModal"
            :prepend-icon="mdiSwapVertical"
          />
        </div>
        <SortOptionsModal
          :is-open="isSortModalOpen"
          :options="sortOptions"
          :selected-value="currentSort"
          @select="handleSortSelect"
          @update:is-open="isSortModalOpen = $event"
        />
      </div>

      <div class="relative">
        <OutlinedButton
          text="Colunas"
          height="32px"
          width="140px"
          @click.stop="toggleColumnModal"
          :prepend-icon="mdiEyeOutline"
        />
        <ColumnSelectorModal
          :is-open="isColumnModalOpen"
          :columns="columnOptions"
          @update:is-open="isColumnModalOpen = $event"
          @apply="handleColumnChange"
        />
      </div>

      <div v-if="canExtractReports">
        <OutlinedButton
          text="Exportar .xlsx"
          height="32px"
          width="180px"
          @click="isExportModalOpen = true"
          :prepend-icon="mdiFileExcel"
          :loading="isExporting"
          :disabled="isExporting || isLoading"
        />
      </div>
    </div>

    <ExportModal
      :is-open="isExportModalOpen"
      :column-groups="visibleColumnGroups"
      :active-filters="activeFiltersForDisplay"
      :raw-filters="activeFilters"
      :is-exporting="isExporting"
      :total-items="totalItems"
      @update:is-open="isExportModalOpen = $event"
      @confirm="handleExportConfirm"
    />
    <FiltersModal
      :is-open="isFilterModalOpen"
      :active-filters="activeFilters"
      @update:is-open="isFilterModalOpen = $event"
      @apply-filters="handleApplyFilters"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { format, parseISO } from 'date-fns'
import SortOptionsModal from '@/components/Filters/SortOptionsModal.vue'
import ColumnSelectorModal from './ColumnSelectorModal.vue'
import ExportModal from './ExportModal.vue'
import FiltersModal from './FiltersModal.vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { mdiSwapVertical, mdiFileExcel, mdiEyeOutline } from '@mdi/js'

const props = defineProps({
  sortOptions: {
    type: Array,
    required: true
  },
  currentSort: {
    type: String,
    required: true
  },
  columnOptions: {
    type: Array,
    required: true
  },
  visibleColumnGroups: {
    type: Array,
    required: true
  },
  activeFiltersForExport: {
    type: Array,
    required: true
  },
  activeFilters: {
    type: Object,
    required: true
  },
  // web#3255: estado do export (dono: index.vue) e da listagem. Durante a
  // listagem o botão fica desabilitado para o export nunca usar um total velho.
  isExporting: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  // Total da listagem, para o aviso prévio do limite de linhas no ExportModal.
  totalItems: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update:sort', 'update:columns', 'export', 'apply-filters'])

const permissionsStore = usePermissionsStore()

const canExtractReports = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationReportsExtraction,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const isFilterModalOpen = ref(false)
const isSortModalOpen = ref(false)
const isColumnModalOpen = ref(false)
const isExportModalOpen = ref(false)

// O modal fecha ao FIM do request (sucesso ou erro), não ao confirmar. Se o
// usuário já fechou durante o export, não há nada a fazer.
watch(
  () => props.isExporting,
  (now, before) => {
    if (before && !now) isExportModalOpen.value = false
  }
)

const currentSortLabel = computed(
  () => props.sortOptions.find((opt) => opt.value === props.currentSort)?.label || 'Ordenar por'
)

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const activeFiltersForDisplay = computed(() => {
  const filters = []
  const filterLabels = {
    companyIds: 'Empresas',
    healthUnitIds: 'Unidades',
    positionIds: 'Posições',
    doctorIds: 'Médicos',
    operationalGroups: 'Grupos Operacionais',
    startDate: 'Dia inicial',
    endDate: 'Dia final',
    paymentTiming: 'Modalidade de repasse',
    isChecked: 'Possui Check-in',
    hasException: 'Excepcionalidade'
  }

  const paymentTimingLabels = {
    FLOW: 'Fluxo',
    IMMEDIATE: 'À vista'
  }

  const { activeFilters } = props
  if (!activeFilters) return []

  for (const key in activeFilters) {
    const value = activeFilters[key]
    const label = filterLabels[key]

    if (
      label &&
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      let displayValue = value

      if (key === 'startDate' || key === 'endDate') {
        try {
          const date = value instanceof Date ? value : parseISO(value)
          displayValue = format(date, 'dd/MM/yyyy')
        } catch (e) {
          console.error(`Invalid date for filter '${key}':`, value)
          continue
        }
      } else if (key === 'paymentTiming' && paymentTimingLabels[value]) {
        displayValue = paymentTimingLabels[value]
      } else if (key === 'hasException') {
        // Sem este ramo o chip renderizaria o booleano cru ("true"/"false").
        displayValue = value ? 'Com excepcionalidade' : 'Sem excepcionalidade'
      } else if (Array.isArray(value)) {
        displayValue = `${value.length} selecionado(s)`
      }

      filters.push({ label, value: displayValue })
    }
  }

  return filters
})

function toggleSortModal() {
  isColumnModalOpen.value = false
  isSortModalOpen.value = !isSortModalOpen.value
}

function handleSortSelect(value) {
  emit('update:sort', value)
  isSortModalOpen.value = false
}

function toggleColumnModal() {
  isSortModalOpen.value = false
  isColumnModalOpen.value = !isColumnModalOpen.value
}

function handleColumnChange(updatedColumns) {
  emit('update:columns', updatedColumns)
}

function handleExportConfirm(exportPayload) {
  emit('export', exportPayload)
}

function handleApplyFilters(newFilters) {
  emit('apply-filters', newFilters)
}
</script>
